import {useObserver} from 'mobx-react-lite';
import * as React from 'react';
import {FilterOption} from './FilterOption';
import {FilterConfig} from './types';
import styles from './FilterSection.module.sass';

interface FilterSectionProps<T = any> {
  config: FilterConfig<T>;
}

/**
 * Generic filter section component that renders filters based on configuration.
 *
 * This component provides a declarative way to create filter UI:
 * - Automatically handles selection state
 * - Supports regular and icon-based options
 * - Supports "Show All" functionality
 * - Fully reactive with MobX
 *
 * @example
 * ```tsx
 * <FilterSection config={builtByFilterConfig} />
 * ```
 */
export function FilterSection<T = any>({config}: FilterSectionProps<T>) {
  return useObserver(() => {
    const {
      title,
      getOptions,
      getSelectedValue,
      onSelect,
    } = config;

    const options = getOptions();
    const selectedValue = getSelectedValue();

    // Hide entire section if there are no options
    if (options.length === 0) {
      return null;
    }

    return (
      <div className={styles.container}>
        {/* Section title */}
        <h6>{title}</h6>

        {/* Regular filter options */}
        {options.map((option) => (
          <FilterOption
            key={String(option.value)}
            option={option}
            isSelected={selectedValue === option.value}
            onClick={() => onSelect(option.value)}
          />
        ))}
      </div>
    );
  });
}
